/**
 * Curated onboarding checklist for the customer-facing chatbot.
 *
 * Every new tenant gets the same 10 essentials customers always ask about.
 * The tenant types an answer to each; each answer becomes a knowledge-base
 * entry tagged with the prompt's stable `key` (via `onboardingKey`), then
 * passes through the existing draft → approve gate before the bot uses it.
 *
 * These keys are STABLE — changing one detaches the existing entry from its
 * prompt and the checklist will treat that prompt as "not started" again.
 */
export interface OnboardingPrompt {
  /** Stable identifier persisted on the KB entry as `onboardingKey`. */
  key: string;
  /** Customer-facing question — becomes the KB entry's `title` verbatim. */
  question: string;
  /** Category the KB entry inherits. */
  category: string;
  /** Hint shown beneath the question to guide the tenant's answer. */
  help: string;
}

export const ONBOARDING_PROMPTS: OnboardingPrompt[] = [
  {
    key: 'business_hours',
    question: 'What are your business hours?',
    category: 'General',
    help: 'Days and times your team is available, and the time zone.',
  },
  {
    key: 'contact',
    question: 'How can I contact your team?',
    category: 'General',
    help: 'Support email, phone or WhatsApp number customers can use.',
  },
  {
    key: 'location',
    question: 'Where are you located?',
    category: 'General',
    help: 'Physical store / office address, or note if you are online-only.',
  },
  {
    key: 'return_policy',
    question: 'What is your return or exchange policy?',
    category: 'Returns',
    help: 'Return window, item condition required, and how to start one.',
  },
  {
    key: 'refund_time',
    question: 'How long does a refund take?',
    category: 'Returns',
    help: 'Typical time for a refund to reach the customer once approved.',
  },
  {
    key: 'shipping',
    question: 'How long does delivery take and what does it cost?',
    category: 'Shipping',
    help: 'Delivery timeframes and any shipping fees or free-shipping threshold.',
  },
  {
    key: 'delivery_areas',
    question: 'Which areas do you deliver to?',
    category: 'Shipping',
    help: 'Regions / countries you ship to, and any you do not.',
  },
  {
    key: 'payment_methods',
    question: 'What payment methods do you accept?',
    category: 'Billing',
    help: 'Cards, bank transfer, cash on delivery, wallets, etc.',
  },
  {
    key: 'order_tracking',
    question: 'How do I track my order?',
    category: 'Orders',
    help: 'How a customer checks order status / gets a tracking link.',
  },
  {
    key: 'order_changes',
    question: 'Can I change or cancel my order after placing it?',
    category: 'Orders',
    help: 'Whether and how soon a customer can change or cancel an order.',
  },
];
